import React, { useState } from 'react'
import { Session, SessionType } from '@chilipiper/models'
import { Text, Icon, Loader, Box, Flex, ScrollBox } from '@chilipiper/design-system'
import { SvgSearch, SvgArrowLeft } from '@chilipiper/icons/src/design-system'
import { useUserBookingLinkQuery } from '../../useUserBookingLink'
import { ApiBookingLink, BookingLinksListType } from '../../types'
import { useUserBookingLinks } from '../../helpers'
import { BookingLink } from '../BookingLink'
import { toogleFavouriteTemplate } from './helpers'
import { StyledWorkspace, StyledArrowIcon, StyledScrollButton, StyledTextField } from './styles'
import {
  useScroll,
  BOOKING_LINKS_HEIGHT,
  usePersonalSettings,
  useSavePersonalSettings,
  useWorkspaces,
  useGroupedBookingLinks,
} from './hooks'

interface Props {
  onCopyLinkClicked?: (alert: JSX.Element, link?: string) => void
  sectionTitle?: JSX.Element
  session?: SessionType
}

export const BookingLinksSection = ({
  session = Session,
  onCopyLinkClicked,
  sectionTitle,
}: Props) => {
  const userId = session.user().id
  const [search, setSearch] = useState('')
  const { data: workspaces = [], isLoading: isLoadingWorkspaces } = useWorkspaces()
  const { bookingLinks = [], isLoading: isLoadingLinks } = useUserBookingLinks(
    userId,
    BookingLinksListType.All,
    session
  )
  const isMultipleBookingLinks = bookingLinks?.length > 1
  const { bookingLinkUrl: defaultBookingLinkUrl = '' } = useUserBookingLinkQuery()
  const { data: settings, isLoading: isLoadingFavourites } = usePersonalSettings(userId)
  const favouriteTemplates = settings?.favouriteTemplates ?? []
  const { mutate: personalSettingsMutation } = useSavePersonalSettings()
  const { visibleBookingLinks, groupedBookingLinks, favouriteBookingLinks } =
    useGroupedBookingLinks({
      search,
      workspaces,
      bookingLinks,
      favouriteTemplates,
      defaultBookingLinkUrl,
    })
  const { scrollRef, showScrollButton, scroll, onScroll } = useScroll(visibleBookingLinks)

  const bookingLinkItem = (bookingLink: ApiBookingLink) => (
    <BookingLink
      bookingLink={bookingLink}
      data-test-id='BookingLinksSection-booking-link'
      favourite={settings?.favouriteTemplates?.includes(bookingLink?.templateId)}
      key={`${bookingLink?.id}${bookingLink?.workspaceId}${bookingLink?.templateId}`}
      onCopyLinkClicked={onCopyLinkClicked}
      session={session}
      toogleFavourite={() => {
        if (settings) {
          personalSettingsMutation(
            toogleFavouriteTemplate({ templateId: bookingLink.templateId, userSettings: settings })
          )
        }
      }}
    />
  )

  return (
    <Box mb={7}>
      {sectionTitle}
      <Box boxShadow='shadow-bevel-neutral-default' borderRadius={8} pb={1}>
        {isMultipleBookingLinks && (
          <StyledTextField
            id='searchField'
            data-test-id='BookingLinksSection-search'
            value={search}
            placeholder='Search..'
            w='100%'
            h='100%'
            bg='bg/page'
            onChange={e => setSearch(e.target.value)}
            endContent={<Icon pointer w={4} h={4} icon={SvgSearch} color='neutral-dark-32' />}
          />
        )}
        <Box position='relative'>
          <ScrollBox
            ref={scrollRef}
            maxHeight={BOOKING_LINKS_HEIGHT}
            onScroll={onScroll}
            data-test-id='BookingLinksSection-scroll-box'
            borderRadius={4}
          >
            {(isLoadingLinks || isLoadingFavourites || isLoadingWorkspaces) && (
              <Loader height={10} />
            )}
            {favouriteBookingLinks.map(bookingLink => bookingLinkItem(bookingLink))}
            {Object.keys(groupedBookingLinks).map(workspaceName => (
              <StyledWorkspace key={workspaceName}>
                {isMultipleBookingLinks && (
                  <Flex
                    h={9}
                    py={0}
                    px={3}
                    justifyContent='center'
                    flexDirection='column'
                    boxShadow='box-neutral-default'
                    bg='bg/page'
                    color='neutral-dark-80'
                    data-test-id='BookingLinksSection-workspace'
                  >
                    <Text color='neutral-dark-80' fontWeight={600}>
                      {workspaceName}
                    </Text>
                  </Flex>
                )}
                {groupedBookingLinks[workspaceName].map(bookingLink =>
                  bookingLinkItem(bookingLink)
                )}
              </StyledWorkspace>
            ))}
            {showScrollButton && (
              <StyledScrollButton
                p={2}
                mb={3}
                bottom={0}
                left='50%'
                position='absolute'
                justifyContent='center'
                alignItems='center'
                cursor='pointer'
                bg='neutral-dark-80'
                onClick={scroll}
                borderRadius={20}
                data-test-id='BookingLinksSection-scroll-button'
              >
                <StyledArrowIcon w={3} h={3} color='white' icon={SvgArrowLeft} />
                <Text color='white' fontWeight={600}>
                  Show More
                </Text>
              </StyledScrollButton>
            )}
          </ScrollBox>
        </Box>
      </Box>
    </Box>
  )
}
